import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { listActivities } from '../lib/activities';
import {
  COLOR_BG,
  HOMEPAGE_MAX_FEATURED,
  type Activity,
} from '../lib/types';

const CATEGORY_EMOJI: Record<string, string> = {
  'Hoppborg': '🏰',
  'Hinderbana': '🏃',
  'Spel': '🎯',
  'Mat & Mys': '🍿',
};

export default function Activities() {
  const [activities, setActivities] = useState<Activity[]>([]);

  useEffect(() => {
    let alive = true;
    listActivities().then((a) => {
      if (alive) setActivities(a);
    });
    return () => {
      alive = false;
    };
  }, []);

  const preview = useMemo(
    () => activities.filter((a) => a.featured).slice(0, HOMEPAGE_MAX_FEATURED),
    [activities],
  );

  return (
    <section id="aktiviteter" className="relative py-16 sm:py-20 bg-ink text-cream overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-30">
        <div className="absolute top-20 left-1/4 size-96 rounded-full bg-tomato blur-3xl" />
        <div className="absolute bottom-10 right-1/4 size-[28rem] rounded-full bg-sky blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="text-cream">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full bg-cream/10 border border-cream/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider"
            >
              <span className="size-1.5 rounded-full bg-sun" />
              Populära aktiviteter
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02] text-balance max-w-2xl"
            >
              Något <span className="text-sun">studsigt</span> för varje tillfälle.
            </motion.h2>
            <p className="mt-5 text-cream/70 text-lg max-w-xl">
              Ett urval av våra mest bokade aktiviteter — alla testade, säkrade och redo att skapa minnen.
            </p>
          </div>

          <a
            href="/aktiviteter"
            className="hidden lg:inline-flex items-center gap-2 rounded-full border border-cream/20 px-5 py-3 text-sm font-semibold text-cream/90 hover:bg-cream/10 hover:text-cream transition-colors"
          >
            Se alla aktiviteter
            <span aria-hidden>→</span>
          </a>
        </div>

        <motion.div
          layout
          className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          {preview.map((a, i) => (
            <motion.article
              key={a.id}
              layout
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: (i % 8) * 0.04 }}
              whileHover={{ y: -6 }}
              className="group relative rounded-3xl bg-cream text-ink p-5 shadow-pop overflow-hidden flex flex-col"
            >
              <div className={`relative h-52 rounded-2xl ${COLOR_BG[a.color]} overflow-hidden`}>
                {a.image ? (
                  <img
                    src={a.image}
                    alt={a.name}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center">
                    <span className="text-7xl opacity-80">{CATEGORY_EMOJI[a.category] ?? '🎪'}</span>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-ink/40 to-transparent" />
                <span className="absolute top-3 left-3 rounded-full bg-cream/90 backdrop-blur px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink">
                  {a.category}
                </span>
              </div>

              <div className="mt-4">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-xl font-semibold leading-tight">{a.name}</h3>
                  <span className="text-sm font-semibold whitespace-nowrap text-tomato">{a.price.replace('från ', '')}</span>
                </div>
                <p className="mt-1.5 text-sm text-ink/65 leading-relaxed">{a.desc}</p>
              </div>

              <a
                href={`?aktivitet=${encodeURIComponent(a.name)}#kontakt`}
                className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ink underline-offset-4 hover:underline"
              >
                Boka <span className="transition-transform group-hover:translate-x-1">→</span>
              </a>
            </motion.article>
          ))}
        </motion.div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="/aktiviteter"
            className="inline-flex items-center gap-2 rounded-full bg-sun text-ink px-7 py-4 text-base font-semibold shadow-bouncy hover:-translate-y-0.5 transition-transform"
          >
            Se alla aktiviteter →
          </a>
          <a
            href="#kontakt"
            className="inline-flex items-center gap-2 rounded-full border border-cream/25 text-cream px-7 py-4 text-base font-semibold hover:bg-cream/10 transition-colors"
          >
            Kontakta oss
          </a>
        </div>
      </div>
    </section>
  );
}
